import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { FranklinErrorScreen } from "./FranklinErrorPage";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Report to Sentry
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    // Handle ChunkLoadError by auto-reloading (once) to get fresh chunks
    // This commonly happens after deployments when users have cached HTML
    // referencing old chunk filenames that no longer exist
    if (this.isChunkLoadError(error)) {
      const hasReloaded = sessionStorage.getItem('chunk_error_reloaded');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_error_reloaded', 'true');
        window.location.reload();
        return;
      }
      // If we've already reloaded once, clear the flag and show the error
      sessionStorage.removeItem('chunk_error_reloaded');
    }
  }

  private isChunkLoadError(error: Error): boolean {
    // Detect various chunk loading error patterns
    return (
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Failed to fetch dynamically imported module')
    );
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      /*
        One error screen for the whole app. A thrown render error and a route
        that failed to load are the same event to the person looking at it,
        so they get the same page rather than two designs that happen to say
        similar things.
      */
      return (
        <div data-testid="error-boundary-container">
          <FranklinErrorScreen error={this.state.error} />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
